import { createSelector } from 'reselect';

import { RootModel } from '../models';
import {
  PageContentTypes,
  ProjectItemTypes,
  ProjectLayoutTypes,
  ProjectTypes,
  StoreImageTypes,
} from '../../types';
import { CONTENT_KEY, PROJECT_ITEM_TYPE } from '../../commons/constants';
import { isValidAmenity } from '../../utils/logicUtils';

export const getProject = createSelector<
  RootModel,
  { [key: string]: ProjectTypes },
  number,
  ProjectTypes
>(
  (state) => state.projectEntity.ids,
  (state) => state.projectModel.projectId,
  (ids, id) => ids[id]
);

type ProjectLayoutConfigTypes = {
  layoutImage?: StoreImageTypes;
  layoutItems?: ProjectLayoutTypes[];
};

export const getProjectLayoutConfig = createSelector<
  RootModel,
  { [key: string]: ProjectTypes },
  { [key: string]: ProjectItemTypes },
  string,
  number,
  ProjectLayoutConfigTypes
>(
  (state) => state.projectEntity.ids,
  (state) => state.projectItemEntity.ids,
  (state) => state.projectModel.contentActiveKey,
  (state) => state.projectModel.projectId,
  (projectIds, projectItemIds, contentActiveKey, id) => {
    const project = projectIds[id];

    if (!project || !project.layoutItems) return {};

    const layoutItems = [];
    project.layoutItems.forEach((layoutItem) => {
      const item = projectItemIds[layoutItem.projectItemId];
      if (!item) return;

      if (
        contentActiveKey === CONTENT_KEY.SITEPLAN.key &&
        (item.type === PROJECT_ITEM_TYPE.BUILDING || item.type === PROJECT_ITEM_TYPE.VILLA_BLOCK)
      ) {
        layoutItems.push({ ...layoutItem, projectItem: item });
      }

      if (contentActiveKey === CONTENT_KEY.AMENITIES.key && isValidAmenity(item)) {
        layoutItems.push({ ...layoutItem, projectItem: item });
      }
    });

    return { layoutImage: project.layoutImage, layoutItems };
  }
);

export const getActiveContent = createSelector<RootModel, string, PageContentTypes>(
  (state) => state.projectModel.contentActiveKey,
  (activeKey) => CONTENT_KEY[activeKey]
);

export const getConfigByActiveContentKey = createSelector<
  RootModel,
  { [key: string]: ProjectTypes },
  number,
  string,
  string[]
>(
  (state) => state.projectEntity.ids,
  (state) => state.projectModel.projectId,
  (state) => state.projectModel.contentActiveKey,
  (ids, id, contentActiveKey) => {
    const project = ids[id];
    if (!project || !project.configs || !project.configs.length) return [];

    switch (contentActiveKey) {
      case CONTENT_KEY.ABOUT.key:
        return [
          CONTENT_KEY.LOCATION.configKey,
          CONTENT_KEY.AMENITIES.configKey,
          CONTENT_KEY.SITEPLAN.configKey,
        ].filter((key) => {
          const item = project.configs.find((configItem) => configItem.type === key);
          return item;
        });

      case CONTENT_KEY.HOME_PROFILE.key:
        return [CONTENT_KEY.LOCATION.configKey, CONTENT_KEY.AMENITIES.configKey].filter((key) => {
          const item = project.configs.find((configItem) => configItem.type === key);
          return item;
        });

      default:
        return [];
    }
  }
);

export const getProjectConfigLocation = createSelector<
  RootModel,
  { [key: string]: ProjectTypes },
  number,
  { mapStyle?: string; satelliteMode?: boolean; numberOfDisplayItem?: number }
>(
  (state) => state.projectEntity.ids,
  (state) => state.projectModel.projectId,
  (ids, id) => {
    const project: ProjectTypes = ids[id];
    if (!project || !project.configs || !project.configs.length) {
      return {};
    }

    const data = project.configs.find((item) => item.type === CONTENT_KEY.LOCATION.configKey);

    if (!data) return {};
    return {
      mapStyle: data.mapStyle,
      satelliteMode: data.satelliteMode,
      numberOfDisplayItem: data.displayQuantity,
    };
  }
);

export const getProjectConfigAmenities = createSelector<
  RootModel,
  { [key: string]: ProjectTypes },
  number,
  string,
  { numberOfDisplayItem?: number }
>(
  (state) => state.projectEntity.ids,
  (state) => state.projectModel.projectId,
  (state) => state.projectModel.contentActiveKey,
  (ids, id, contentActiveKey) => {
    const project: ProjectTypes = ids[id];
    if (
      !project ||
      !project.configs ||
      !project.configs.length ||
      contentActiveKey !== CONTENT_KEY.AMENITIES.configKey
    ) {
      return {};
    }

    const data = project.configs.find((item) => item.type === CONTENT_KEY.AMENITIES.configKey);

    if (!data) return {};
    return { numberOfDisplayItem: data.displayQuantity };
  }
);

export const getProjectConfigSitePlan = createSelector<
  RootModel,
  { [key: string]: ProjectTypes },
  number,
  string,
  { numberOfDisplayItem?: number }
>(
  (state) => state.projectEntity.ids,
  (state) => state.projectModel.projectId,
  (state) => state.projectModel.contentActiveKey,
  (ids, id, contentActiveKey) => {
    const project: ProjectTypes = ids[id];

    if (
      !project ||
      !project.configs ||
      !project.configs.length ||
      contentActiveKey !== CONTENT_KEY.SITEPLAN.configKey
    ) {
      return {};
    }

    const data = project.configs.find((item) => item.type === CONTENT_KEY.SITEPLAN.configKey);

    if (!data) {
      return {};
    }

    return { numberOfDisplayItem: data.displayQuantity };
  }
);
